import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

/** Thin client for /api/contacts/bulk-email/* — a contacts.bulk_email-gated
 *  door into the existing campaign engine (src/api-contacts/bulk-email.js). */

export interface CreateBulkEmailPayload {
  contactIds: string[];
  subject: string;
  bodyHtml: string;
}

export interface CreateBulkEmailResult {
  ok: boolean;
  campaignId: string;
  eligibleCount: number;
  skipped: { noEmail: number; suppressed: number };
}

export interface BulkEmailPreview {
  ok: boolean;
  issues: string[];
  audience: number;
  skipped: Record<string, number>;
  preview: { subject: string; html: string };
}

export interface BulkEmailStats {
  campaign: {
    id: string; name: string; subject: string; status: string; createdAt: string;
    stats: { audience: number; sent: number; bounced: number; failed: number };
  };
  recipients: Record<string, number>;
}

export function useCreateContactsBulkEmail() {
  return useMutation({
    mutationFn: async (payload: CreateBulkEmailPayload) => {
      const { data } = await api.post<CreateBulkEmailResult>('/api/contacts/bulk-email/campaigns', payload);
      return data;
    },
  });
}

export function useBulkEmailPreview(campaignId: string | null) {
  return useQuery({
    queryKey: ['contacts-bulk-email', 'preview', campaignId],
    queryFn: async () => {
      const { data } = await api.get<BulkEmailPreview>(`/api/contacts/bulk-email/campaigns/${campaignId}/preview`);
      return data;
    },
    enabled: !!campaignId,
  });
}

export function useTestBulkEmail(campaignId: string | null) {
  return useMutation({
    mutationFn: async (to: string) => {
      const { data } = await api.post<{ ok: boolean; to: string }>(
        `/api/contacts/bulk-email/campaigns/${campaignId}/test`, { to },
      );
      return data;
    },
  });
}

export function useSendBulkEmail(campaignId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ ok: boolean; status: string }>(
        `/api/contacts/bulk-email/campaigns/${campaignId}/send`,
      );
      return data;
    },
  });
}

/** Polled while a send is in progress, to drive the progress state. */
export function useBulkEmailStats(campaignId: string | null, { poll = false }: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: ['contacts-bulk-email', 'stats', campaignId],
    queryFn: async () => {
      const { data } = await api.get<BulkEmailStats>(`/api/contacts/bulk-email/campaigns/${campaignId}/stats`);
      return data;
    },
    enabled: !!campaignId,
    refetchInterval: poll ? 2500 : false,
  });
}
